import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import { BACKEND_SCRUM_ROLES } from '../shared/appConstants';

const buildDailyStandupReportUrl = (basePath, projectId, date) => {
  const queryParams = new URLSearchParams();

  if (projectId) queryParams.append('project_id', projectId);
  if (date) queryParams.append('date', date);

  const queryString = queryParams.toString();
  return `${basePath}${queryString ? `?${queryString}` : ''}`;
};

const normalizeName = (user = {}) => {
  if (user?.name) return user.name;
  const parts = [user?.first_name, user?.last_name].filter(Boolean);
  return parts.join(' ').trim();
};

const buildInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';

const normalizeTeamMembers = (payload) => {
  const candidateLists = [
    payload?.assignees,
    payload?.users,
    payload?.project_users,
    payload?.team_members,
    payload?.members,
    Array.isArray(payload) ? payload : null,
  ].filter(Array.isArray);

  const rawMembers = candidateLists[0] || [];

  return rawMembers
    .map((member) => {
      if (!member) return null;
      const name = normalizeName(member);
      return {
        ...member,
        id: member.id || member.user_id,
        name: name || member.email || 'Unassigned User',
        avatar: member.avatar || member.initials || buildInitials(name || member.email || 'User'),
        profile_picture: member.profile_picture || member.profilePicture || member.avatar_url || null,
      };
    })
    .filter(Boolean);
};

const normalizeStandupDetails = (payload) => {
  const data = payload?.data || payload || {};
  const additionalNotes = Array.isArray(data.additional_notes)
    ? data.additional_notes
    : Array.isArray(data.notes)
      ? data.notes
      : [];

  return {
    user: data.user || null,
    filter: data.filter || 'week',
    worklog: Array.isArray(data.worklog) ? data.worklog : [],
    yesterday_plan: Array.isArray(data.yesterday_plan) ? data.yesterday_plan : [],
    today_plan: Array.isArray(data.today_plan) ? data.today_plan : [],
    blockers: Array.isArray(data.blockers) ? data.blockers : [],
    notes: additionalNotes,
    additional_notes: additionalNotes,
  };
};

export const fetchDailyStandupReport = createAsyncThunk(
  'dailyStandup/fetchDailyStandupReport',
  async ({ projectId, date }, { rejectWithValue }) => {
    try {
      const response = await api.get(buildDailyStandupReportUrl(ENDPOINTS.DAILY_STANDUP_REPORT, projectId, date));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch daily standup report'
      );
    }
  }
);

export const sendDailyStandupReportEmail = createAsyncThunk(
  'dailyStandup/sendDailyStandupReportEmail',
  async ({ projectId, date }, { rejectWithValue }) => {
    try {
      const response = await api.get(buildDailyStandupReportUrl(ENDPOINTS.DAILY_STANDUP_REPORT_EMAIL, projectId, date));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to send daily standup report email'
      );
    }
  }
);

export const fetchDailyStandupScrumRole = createAsyncThunk(
  'dailyStandup/fetchDailyStandupScrumRole',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.SCRUM_ROLE(projectId));
      return response.data?.data || response.data || null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch daily standup role'
      );
    }
  }
);

export const fetchDailyStandupTeamMembers = createAsyncThunk(
  'dailyStandup/fetchDailyStandupTeamMembers',
  async ({ projectIds } = {}, { rejectWithValue }) => {
    try {
      const projectIdsParam = Array.isArray(projectIds) ? projectIds.join(',') : projectIds;
      const url = `${ENDPOINTS.TIMESHEET_DROPDOWNS('users')}&status=ACTIVE${projectIdsParam ? `&projectIds=${projectIdsParam}` : ''}`;
      const response = await api.get(url);
      const data = response.data?.data || response.data || {};
      return normalizeTeamMembers(data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch team members'
      );
    }
  }
);

export const fetchDailyStandupLog = createAsyncThunk(
  'dailyStandup/fetchDailyStandupLog',
  async ({ projectIds, userId, worklogFilter }, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.DAILY_STANDUP_LOG(projectIds, userId, worklogFilter));
      return normalizeStandupDetails(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch daily standup log'
      );
    }
  }
);

export const addDailyStandupNote = createAsyncThunk(
  'dailyStandup/addDailyStandupNote',
  async ({ projectId, targetUserId, notes }, { rejectWithValue }) => {
    try {
      const payload = {
        project_id: projectId,
        user_id: targetUserId,
        notes: typeof notes === 'string' ? notes : (notes?.note || notes?.notes || notes?.content || String(notes || '')),
      };

      const response = await api.post(ENDPOINTS.DAILY_STANDUP_ADD_NOTE, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to add note'
      );
    }
  }
);

export const updateDailyStandupNote = createAsyncThunk(
  'dailyStandup/updateDailyStandupNote',
  async ({ noteId, projectId, targetUserId, notes }, { rejectWithValue }) => {
    try {
      const payload = {
        notes: typeof notes === 'string' ? notes : (notes?.note || notes?.notes || notes?.content || String(notes || '')),
      };

      const response = await api.put(ENDPOINTS.DAILY_STANDUP_UPDATE_NOTE(noteId), payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update note'
      );
    }
  }
);

export const deleteDailyStandupNote = createAsyncThunk(
  'dailyStandup/deleteDailyStandupNote',
  async (noteId, { rejectWithValue }) => {
    try {
      const response = await api.delete(ENDPOINTS.DAILY_STANDUP_DELETE_NOTE(noteId));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to delete note'
      );
    }
  }
);

const initialState = {
  report: null,
  loading: false,
  error: null,
  emailLoading: false,
  emailError: null,
  scrumRole: null,
  scrumRoleLoading: false,
  scrumRoleError: null,
  teamMembers: [],
  teamMembersLoading: false,
  teamMembersError: null,
  standupLog: null,
  standupLogLoading: false,
  standupLogError: null,
  addNoteLoading: false,
  addNoteError: null,
  updateNoteLoading: false,
  updateNoteError: null,
  deleteNoteLoading: false,
  deleteNoteError: null,
};

const dailyStandupSlice = createSlice({
  name: 'dailyStandup',
  initialState,
  reducers: {
    clearDailyStandupData: (state) => {
      state.report = null;
      state.error = null;
      state.loading = false;
      state.emailLoading = false;
      state.emailError = null;
      state.scrumRole = null;
      state.scrumRoleLoading = false;
      state.scrumRoleError = null;
      state.teamMembers = [];
      state.teamMembersLoading = false;
      state.teamMembersError = null;
      state.standupLog = null;
      state.standupLogLoading = false;
      state.standupLogError = null;
      state.addNoteLoading = false;
      state.addNoteError = null;
      state.updateNoteLoading = false;
      state.updateNoteError = null;
      state.deleteNoteLoading = false;
      state.deleteNoteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailyStandupReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyStandupReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload?.data || action.payload;
      })
      .addCase(fetchDailyStandupReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(sendDailyStandupReportEmail.pending, (state) => {
        state.emailLoading = true;
        state.emailError = null;
      })
      .addCase(sendDailyStandupReportEmail.fulfilled, (state) => {
        state.emailLoading = false;
      })
      .addCase(sendDailyStandupReportEmail.rejected, (state, action) => {
        state.emailLoading = false;
        state.emailError = action.payload || action.error.message;
      })
      .addCase(fetchDailyStandupScrumRole.pending, (state) => {
        state.scrumRoleLoading = true;
        state.scrumRoleError = null;
      })
      .addCase(fetchDailyStandupScrumRole.fulfilled, (state, action) => {
        state.scrumRoleLoading = false;
        state.scrumRole = action.payload || null;
      })
      .addCase(fetchDailyStandupScrumRole.rejected, (state, action) => {
        state.scrumRoleLoading = false;
        state.scrumRoleError = action.payload || action.error.message;
      })
      .addCase(fetchDailyStandupTeamMembers.pending, (state) => {
        state.teamMembersLoading = true;
        state.teamMembersError = null;
      })
      .addCase(fetchDailyStandupTeamMembers.fulfilled, (state, action) => {
        state.teamMembersLoading = false;
        state.teamMembers = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchDailyStandupTeamMembers.rejected, (state, action) => {
        state.teamMembersLoading = false;
        state.teamMembersError = action.payload || action.error.message;
      })
      .addCase(fetchDailyStandupLog.pending, (state) => {
        state.standupLogLoading = true;
        state.standupLogError = null;
      })
      .addCase(fetchDailyStandupLog.fulfilled, (state, action) => {
        state.standupLogLoading = false;
        state.standupLog = action.payload || null;
      })
      .addCase(fetchDailyStandupLog.rejected, (state, action) => {
        state.standupLogLoading = false;
        state.standupLogError = action.payload || action.error.message;
      })
      .addCase(addDailyStandupNote.pending, (state) => {
        state.addNoteLoading = true;
        state.addNoteError = null;
      })
      .addCase(addDailyStandupNote.fulfilled, (state) => {
        state.addNoteLoading = false;
      })
      .addCase(addDailyStandupNote.rejected, (state, action) => {
        state.addNoteLoading = false;
        state.addNoteError = action.payload || action.error.message;
      })
      .addCase(updateDailyStandupNote.pending, (state) => {
        state.updateNoteLoading = true;
        state.updateNoteError = null;
      })
      .addCase(updateDailyStandupNote.fulfilled, (state) => {
        state.updateNoteLoading = false;
      })
      .addCase(updateDailyStandupNote.rejected, (state, action) => {
        state.updateNoteLoading = false;
        state.updateNoteError = action.payload || action.error.message;
      })
      .addCase(deleteDailyStandupNote.pending, (state) => {
        state.deleteNoteLoading = true;
        state.deleteNoteError = null;
      })
      .addCase(deleteDailyStandupNote.fulfilled, (state) => {
        state.deleteNoteLoading = false;
      })
      .addCase(deleteDailyStandupNote.rejected, (state, action) => {
        state.deleteNoteLoading = false;
        state.deleteNoteError = action.payload || action.error.message;
      });
  },
});

export const { clearDailyStandupData } = dailyStandupSlice.actions;
export default dailyStandupSlice.reducer;
export { BACKEND_SCRUM_ROLES };
